import { Box, Text } from "@chakra-ui/react-v3";
import {
  MedicationAdministration,
  MedicationDispense,
  MedicationRequest,
  MedicationStatement,
} from "@medplum/fhirtypes";
import { Fragment } from "react";
import { ResourceCardLayout } from "../components/resource-card-layout";
import { getDocIdExtension } from "../utils/document-utils";

export type MedicationTimelineItemProps = {
  resource: MedicationAdministration | MedicationDispense | MedicationStatement | MedicationRequest;
};

type EventInfo = {
  badge: {
    text: string;
    colorPalette: string;
  };
  date: string;
  details: Array<{
    label: string;
    value: string | React.ReactNode;
  }>;
};

/**
 * Medication-specific timeline item that displays medication events with event type badge,
 * dosage, and other relevant details.
 */
export function MedicationTimelineItem({ resource }: MedicationTimelineItemProps) {
  const docId = getDocIdExtension(resource);
  const eventInfo = getMedicationResourceInfo(resource);

  return (
    <ResourceCardLayout
      date={eventInfo.date}
      docId={docId}
      resourceType={resource.resourceType}
      resources={[resource]}
      badge={eventInfo.badge}
    >
      {/* Event details in grid layout */}
      <Box
        display="grid"
        gridTemplateColumns="auto 1fr"
        gap={2}
        rowGap={2}
        columnGap={4}
        alignItems="start"
      >
        {eventInfo.details.map((detail, index) => (
          <Fragment key={index}>
            <Text fontSize="sm" color="fg.muted" fontWeight="medium">
              {detail.label}:
            </Text>
            <Box>
              {typeof detail.value === "string" ? (
                <Text fontSize="sm" color="fg.emphasized">
                  {detail.value}
                </Text>
              ) : (
                detail.value
              )}
            </Box>
          </Fragment>
        ))}
      </Box>
    </ResourceCardLayout>
  );
}

function getMedicationResourceInfo(
  resource: MedicationAdministration | MedicationDispense | MedicationStatement | MedicationRequest
): EventInfo {
  const resourceType = resource.resourceType;

  switch (resourceType) {
    case "MedicationAdministration": {
      const admin = resource as MedicationAdministration;
      const adminDate =
        admin.effectivePeriod?.start ?? admin.effectiveDateTime ?? admin.effectivePeriod?.end;
      const hasDosage = admin.dosage?.dose?.value && admin.dosage?.dose?.unit;

      return {
        badge: {
          text: "Administered",
          colorPalette: "blue",
        },
        date: adminDate || "Unknown",
        details: [
          {
            label: "Route",
            value: admin.dosage?.route?.text || "-",
          },
          {
            label: "Dosage",
            value: hasDosage ? `${admin.dosage?.dose?.value} ${admin.dosage?.dose?.unit}` : "-",
          },
        ],
      };
    }

    case "MedicationDispense": {
      const dispense = resource as MedicationDispense;
      return {
        badge: {
          text: "Dispensed",
          colorPalette: "blue",
        },
        date: dispense.whenHandedOver || new Date().toISOString(),
        details: [
          {
            label: "Quantity",
            value:
              dispense.quantity?.value && dispense.quantity?.unit
                ? `${dispense.quantity.value} ${dispense.quantity.unit}`
                : "-",
          },
        ],
      };
    }

    case "MedicationStatement": {
      const statement = resource as MedicationStatement;
      const statementDate =
        statement.dateAsserted ??
        statement.effectivePeriod?.start ??
        statement.effectiveDateTime ??
        statement.effectivePeriod?.end;

      return {
        badge: {
          text: "Documented",
          colorPalette: "gray",
        },
        date: statementDate || new Date().toISOString(),
        details: [
          {
            label: "Instructions",
            value: statement.dosage?.[0]?.text || "-",
          },
        ],
      };
    }

    case "MedicationRequest": {
      const request = resource as MedicationRequest;
      return {
        badge: {
          text: "Requested",
          colorPalette: "blue",
        },
        date: request.authoredOn || "Unknown",
        details: [
          {
            label: "Note",
            value: request.note?.[0]?.text || "-",
          },
        ],
      };
    }

    default:
      return {
        badge: {
          text: "Unknown",
          colorPalette: "gray",
        },
        date: new Date().toISOString(),
        details: [],
      };
  }
}
